import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { AlertTriangle, Pencil, Plus, RefreshCw, ShieldAlert, Sparkles, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Page, PageHeader } from '@/components/shell'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { api, ApiError, type LaunchVanityConfig } from '@/lib/api'
import { useCan } from '@/lib/session'

export const Route = createFileRoute('/dashboard/contracts/vanity')({ component: LaunchVanityPage })

type SuffixForm = {
  untaxedEnabled: boolean
  untaxedSuffix: string
  taxedEnabled: boolean
  taxedSuffix: string
  reason: string
}
type SensitiveMatchType = 'exact' | 'contains' | 'prefix' | 'suffix'
type SensitiveRuleTarget = 'name' | 'symbol' | 'both'
type SensitiveRule = {
  value: string
  matchType: SensitiveMatchType
  target: SensitiveRuleTarget
}
type EditableSensitiveRule = SensitiveRule & { key: number }
type SensitiveRulesForm = { rules: EditableSensitiveRule[]; reason: string }

let nextRuleKey = 1
const editableRule = (rule: SensitiveRule): EditableSensitiveRule => ({
  ...rule,
  key: nextRuleKey++,
})

const MATCH_TYPE_LABELS: Record<SensitiveMatchType, string> = {
  exact: '完全匹配',
  contains: '包含匹配',
  prefix: '前缀匹配',
  suffix: '后缀匹配',
}

const TARGET_LABELS: Record<SensitiveRuleTarget, string> = {
  name: '仅名称',
  symbol: '仅代码',
  both: '名称和代码',
}

export function normalizeSensitiveRules(rules: readonly SensitiveRule[]): SensitiveRule[] {
  const normalized: SensitiveRule[] = []
  const seen = new Set<string>()
  for (const rule of rules) {
    const value = rule.value.normalize('NFKC').trim()
    if (!value) continue
    const key = `${value.toLocaleLowerCase('und')}\u0000${rule.matchType}\u0000${rule.target}`
    if (!seen.has(key)) {
      seen.add(key)
      normalized.push({ value, matchType: rule.matchType, target: rule.target })
    }
  }
  return normalized
}

function initialSuffixForm(config: LaunchVanityConfig): SuffixForm {
  return {
    untaxedEnabled: config.untaxedSuffix !== null,
    untaxedSuffix: config.untaxedSuffix ?? '',
    taxedEnabled: config.taxedSuffix !== null,
    taxedSuffix: config.taxedSuffix ?? '',
    reason: '',
  }
}

function initialSensitiveRulesForm(config: LaunchVanityConfig): SensitiveRulesForm {
  return { rules: config.sensitiveRules.map(editableRule), reason: '' }
}

function errorMessage(error: unknown): string {
  return error instanceof ApiError || error instanceof Error ? error.message : '请稍后重试'
}

function LaunchVanityPage() {
  const canManage = useCan()('contract', 'manage')
  const qc = useQueryClient()
  const [suffixForm, setSuffixForm] = useState<SuffixForm | null>(null)
  const [sensitiveRulesForm, setSensitiveRulesForm] = useState<SensitiveRulesForm | null>(null)
  const config = useQuery({
    queryKey: ['launch-vanity-config'],
    queryFn: () => api.get<LaunchVanityConfig>('/contracts/vanity/config'),
    retry: false,
  })

  const suffixUpdate = useMutation({
    mutationFn: async () => {
      if (!suffixForm || !config.data) throw new Error('地址后缀表单尚未初始化')
      return api.patch<LaunchVanityConfig>('/contracts/vanity/config', {
        untaxedSuffix: suffixForm.untaxedEnabled
          ? suffixForm.untaxedSuffix.trim().toLowerCase()
          : null,
        taxedSuffix: suffixForm.taxedEnabled ? suffixForm.taxedSuffix.trim().toLowerCase() : null,
        sensitiveWords: config.data.sensitiveWords,
        sensitiveRules: config.data.sensitiveRules,
        reason: suffixForm.reason.trim(),
      })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['launch-vanity-config'] })
      setSuffixForm(null)
      toast.success('地址后缀配置已更新')
    },
    onError: (error) => toast.error('地址后缀更新失败', { description: errorMessage(error) }),
  })

  const sensitiveRulesUpdate = useMutation({
    mutationFn: async () => {
      if (!sensitiveRulesForm || !config.data) throw new Error('敏感规则表单尚未初始化')
      const rules = normalizeSensitiveRules(sensitiveRulesForm.rules)
      return api.patch<LaunchVanityConfig>('/contracts/vanity/config', {
        untaxedSuffix: config.data.untaxedSuffix,
        taxedSuffix: config.data.taxedSuffix,
        sensitiveWords: rules.map((rule) => rule.value),
        sensitiveRules: rules,
        reason: sensitiveRulesForm.reason.trim(),
      })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['launch-vanity-config'] })
      setSensitiveRulesForm(null)
      toast.success('敏感规则配置已更新')
    },
    onError: (error) => toast.error('敏感规则更新失败', { description: errorMessage(error) }),
  })

  const suffixValid = Boolean(
    suffixForm &&
    (!suffixForm.untaxedEnabled || /^[0-9a-fA-F]{4}$/.test(suffixForm.untaxedSuffix.trim())) &&
    (!suffixForm.taxedEnabled || /^[0-9a-fA-F]{4}$/.test(suffixForm.taxedSuffix.trim())) &&
    suffixForm.reason.trim().length >= 10 &&
    suffixForm.reason.trim().length <= 200,
  )
  const normalizedRules = sensitiveRulesForm
    ? normalizeSensitiveRules(sensitiveRulesForm.rules)
    : []
  const sensitiveRulesValid = Boolean(
    sensitiveRulesForm &&
    sensitiveRulesForm.rules.length <= 200 &&
    normalizedRules.length === sensitiveRulesForm.rules.length &&
    normalizedRules.every(
      (rule) => rule.value.length <= 64 && !/[\u0000-\u001f\u007f]/.test(rule.value),
    ) &&
    sensitiveRulesForm.reason.trim().length >= 10 &&
    sensitiveRulesForm.reason.trim().length <= 200,
  )

  return (
    <Page>
      <PageHeader
        title="发币策略"
        description="地址后缀与代币名称/代码敏感词独立配置。保存后对下一次创建请求立即生效，已创建代币不受影响。"
        actions={
          <Button
            variant="outline"
            onClick={() => void config.refetch()}
            disabled={config.isFetching}
          >
            <RefreshCw className={config.isFetching ? 'animate-spin' : ''} />
            刷新
          </Button>
        }
      />

      {config.isPending && (
        <div className="grid gap-4 lg:grid-cols-2" aria-label="正在加载发币策略配置">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      )}
      {config.isError && (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>无法读取发币策略配置</AlertTitle>
          <AlertDescription>{errorMessage(config.error)}</AlertDescription>
        </Alert>
      )}
      {config.isSuccess && (
        <>
          <div className="grid items-start gap-4 lg:grid-cols-2">
            <SuffixPolicyCard
              config={config.data}
              canManage={canManage}
              onEdit={() => setSuffixForm(initialSuffixForm(config.data))}
            />
            <SensitiveWordsCard
              rules={config.data.sensitiveRules}
              canManage={canManage}
              onEdit={() => setSensitiveRulesForm(initialSensitiveRulesForm(config.data))}
            />
          </div>
          <p className="text-xs text-mut2">
            {config.data.updatedAt
              ? `最近由 ${config.data.updatedBy ?? '未知管理员'} 于 ${new Date(config.data.updatedAt).toLocaleString('zh-CN')} 更新`
              : '尚未保存过配置；当前使用平台默认值：非税币 bbbb，税币 eeee。'}
          </p>
        </>
      )}

      <Dialog
        open={suffixForm !== null}
        onOpenChange={(open) => !open && !suffixUpdate.isPending && setSuffixForm(null)}
      >
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>修改地址后缀</DialogTitle>
            <DialogDescription>
              分别配置非税币与税币的 CREATE2 地址尾号；不会修改敏感词配置。
            </DialogDescription>
          </DialogHeader>
          {suffixForm && (
            <div className="space-y-5 py-2">
              <SuffixField
                id="untaxed-suffix"
                label="非税币后缀"
                enabled={suffixForm.untaxedEnabled}
                value={suffixForm.untaxedSuffix}
                onEnabled={(untaxedEnabled) => setSuffixForm({ ...suffixForm, untaxedEnabled })}
                onValue={(untaxedSuffix) => setSuffixForm({ ...suffixForm, untaxedSuffix })}
              />
              <SuffixField
                id="taxed-suffix"
                label="税币后缀"
                enabled={suffixForm.taxedEnabled}
                value={suffixForm.taxedSuffix}
                onEnabled={(taxedEnabled) => setSuffixForm({ ...suffixForm, taxedEnabled })}
                onValue={(taxedSuffix) => setSuffixForm({ ...suffixForm, taxedSuffix })}
              />
              <ReasonField
                id="suffix-reason"
                value={suffixForm.reason}
                onChange={(reason) => setSuffixForm({ ...suffixForm, reason })}
              />
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSuffixForm(null)}
              disabled={suffixUpdate.isPending}
            >
              取消
            </Button>
            <Button
              onClick={() => suffixUpdate.mutate()}
              disabled={!suffixValid || suffixUpdate.isPending}
            >
              {suffixUpdate.isPending ? '保存中…' : '确认保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={sensitiveRulesForm !== null}
        onOpenChange={(open) =>
          !open && !sensitiveRulesUpdate.isPending && setSensitiveRulesForm(null)
        }
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>修改敏感规则</DialogTitle>
            <DialogDescription>
              每条规则可选择匹配方式和作用字段；匹配忽略大小写并使用 NFKC
              归一化，不会修改地址后缀配置。
            </DialogDescription>
          </DialogHeader>
          {sensitiveRulesForm && (
            <div className="space-y-5 py-2">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <Label>名称/代码敏感规则</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={sensitiveRulesForm.rules.length >= 200}
                    onClick={() =>
                      setSensitiveRulesForm({
                        ...sensitiveRulesForm,
                        rules: [
                          ...sensitiveRulesForm.rules,
                          editableRule({ value: '', matchType: 'contains', target: 'both' }),
                        ],
                      })
                    }
                  >
                    <Plus />
                    添加规则
                  </Button>
                </div>
                {sensitiveRulesForm.rules.length > 0 ? (
                  <div className="space-y-3">
                    {sensitiveRulesForm.rules.map((rule, index) => (
                      <SensitiveRuleRow
                        key={rule.key}
                        rule={rule}
                        index={index}
                        onChange={(next) =>
                          setSensitiveRulesForm({
                            ...sensitiveRulesForm,
                            rules: sensitiveRulesForm.rules.map((current) =>
                              current.key === rule.key ? { ...next, key: rule.key } : current,
                            ),
                          })
                        }
                        onRemove={() =>
                          setSensitiveRulesForm({
                            ...sensitiveRulesForm,
                            rules: sensitiveRulesForm.rules.filter(
                              (current) => current.key !== rule.key,
                            ),
                          })
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <div className="surface-inset rounded-apple p-5 text-center text-sm text-mut2">
                    尚未配置规则，保存后将不拦截名称或代码。
                  </div>
                )}
                <p className="text-xs text-mut2">
                  每条内容 1–64 个字符，最多 200 条，不允许完全重复。当前{' '}
                  {sensitiveRulesForm.rules.length} 条。
                </p>
              </div>
              <ReasonField
                id="sensitive-rules-reason"
                value={sensitiveRulesForm.reason}
                onChange={(reason) => setSensitiveRulesForm({ ...sensitiveRulesForm, reason })}
              />
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSensitiveRulesForm(null)}
              disabled={sensitiveRulesUpdate.isPending}
            >
              取消
            </Button>
            <Button
              onClick={() => sensitiveRulesUpdate.mutate()}
              disabled={!sensitiveRulesValid || sensitiveRulesUpdate.isPending}
            >
              {sensitiveRulesUpdate.isPending ? '保存中…' : '确认保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  )
}

function SuffixPolicyCard({
  config,
  canManage,
  onEdit,
}: {
  config: LaunchVanityConfig
  canManage: boolean
  onEdit: () => void
}) {
  return (
    <section className="surface p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-medium">
            <Sparkles className="size-4 text-cyan" />
            地址后缀策略
          </h2>
          <p className="mt-2 text-xs text-mut2">控制税币与非税币的 CREATE2 地址尾号。</p>
        </div>
        {canManage && (
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Pencil />
            修改地址后缀
          </Button>
        )}
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <SuffixValue title="非税币" suffix={config.untaxedSuffix} />
        <SuffixValue title="税币" suffix={config.taxedSuffix} />
      </div>
    </section>
  )
}

function SensitiveWordsCard({
  rules,
  canManage,
  onEdit,
}: {
  rules: SensitiveRule[]
  canManage: boolean
  onEdit: () => void
}) {
  return (
    <section className="surface p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-medium">
            <ShieldAlert className="size-4 text-warn" />
            名称/代码敏感词
          </h2>
          <p className="mt-2 text-xs text-mut2">命中任一规则即拒绝签名，不产生链上交易。</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{rules.length} 条</Badge>
          {canManage && (
            <Button size="sm" variant="outline" onClick={onEdit}>
              <Pencil />
              修改敏感规则
            </Button>
          )}
        </div>
      </div>
      {rules.length > 0 ? (
        <div className="mt-5 space-y-2">
          {rules.map((rule) => (
            <div
              key={`${rule.value.toLocaleLowerCase('und')}:${rule.matchType}:${rule.target}`}
              className="surface-inset flex flex-wrap items-center gap-2 rounded-apple px-3 py-2"
            >
              <span className="min-w-0 flex-1 break-all font-mono text-sm">{rule.value}</span>
              <Badge variant="outline">{MATCH_TYPE_LABELS[rule.matchType]}</Badge>
              <Badge variant="secondary">{TARGET_LABELS[rule.target]}</Badge>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-5 text-sm text-mut2">尚未配置敏感规则。</p>
      )}
    </section>
  )
}

function SensitiveRuleRow({
  rule,
  index,
  onChange,
  onRemove,
}: {
  rule: SensitiveRule
  index: number
  onChange: (rule: SensitiveRule) => void
  onRemove: () => void
}) {
  return (
    <div className="surface-inset grid gap-2 rounded-apple p-3 sm:grid-cols-[minmax(12rem,1fr)_10rem_10rem_auto]">
      <Input
        value={rule.value}
        maxLength={64}
        placeholder="规则内容"
        aria-label={`规则 ${index + 1} 内容`}
        className="font-mono"
        onChange={(event) => onChange({ ...rule, value: event.target.value })}
      />
      <Select
        value={rule.matchType}
        onValueChange={(matchType) =>
          onChange({ ...rule, matchType: matchType as SensitiveMatchType })
        }
      >
        <SelectTrigger className="w-full" aria-label={`规则 ${index + 1} 匹配方式`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(MATCH_TYPE_LABELS).map(([value, label]) => (
            <SelectItem key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={rule.target}
        onValueChange={(target) => onChange({ ...rule, target: target as SensitiveRuleTarget })}
      >
        <SelectTrigger className="w-full" aria-label={`规则 ${index + 1} 作用字段`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(TARGET_LABELS).map(([value, label]) => (
            <SelectItem key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`删除规则 ${index + 1}`}
        onClick={onRemove}
      >
        <Trash2 />
      </Button>
    </div>
  )
}

function SuffixValue({ title, suffix }: { title: string; suffix: string | null }) {
  return (
    <div className="surface-inset rounded-apple p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">{title}</h3>
        <Badge variant={suffix ? 'default' : 'secondary'}>{suffix ? '已启用' : '未启用'}</Badge>
      </div>
      <div className="mt-4 font-mono text-2xl tracking-widest text-txt">
        {suffix ? `…${suffix}` : '随机地址'}
      </div>
    </div>
  )
}

function SuffixField(props: {
  id: string
  label: string
  enabled: boolean
  value: string
  onEnabled: (value: boolean) => void
  onValue: (value: string) => void
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={props.id}>{props.label}</Label>
        <Switch
          checked={props.enabled}
          onCheckedChange={props.onEnabled}
          aria-label={`启用${props.label}`}
        />
      </div>
      <Input
        id={props.id}
        value={props.value}
        disabled={!props.enabled}
        maxLength={4}
        placeholder="例如 0196"
        className="font-mono uppercase"
        onChange={(event) => props.onValue(event.target.value.replace(/[^0-9a-fA-F]/g, ''))}
      />
    </div>
  )
}

function ReasonField({
  id,
  value,
  onChange,
}: {
  id: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>修改理由</Label>
      <Textarea
        id={id}
        value={value}
        maxLength={200}
        placeholder="至少 10 个字符，将写入审计日志"
        onChange={(event) => onChange(event.target.value)}
      />
      <p className="text-xs text-mut2">{value.trim().length}/200</p>
    </div>
  )
}
